import React, { useState } from "react";
import { Check, CheckCircle2, FileText } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { FormSubmissionDialog } from "@/features/hiring-process/form-submission-dialog";
import type { RouterOutputs } from "@/trpc";

export type ApplicationForm =
  RouterOutputs["jobPost"]["getApplicationForms"][number];

function ApplicationFormRow({
  form,
  jobPostId,
  onCompleted,
}: {
  form: ApplicationForm;
  jobPostId: string;
  onCompleted: () => void;
}) {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <li
      className={cn(
        "flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center",
        form.submitted
          ? "border-green-100 bg-green-50/50"
          : "border-slate-200 bg-white",
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full",
            form.submitted ? "bg-green-100" : "bg-primary/5",
          )}
        >
          {form.submitted ? (
            <CheckCircle2 size={16} className="text-green-600" />
          ) : (
            <FileText size={15} className="text-primary" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug break-words text-slate-900">
            {form.title}
          </p>
          {form.description && (
            <p className="mt-1 text-xs leading-relaxed break-words text-slate-500">
              {form.description}
            </p>
          )}
        </div>
      </div>

      {form.submitted ? (
        <span className="inline-flex shrink-0 items-center gap-1 self-start rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700 sm:self-auto">
          <Check size={11} />
          Submitted
        </span>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            className="h-10 w-full sm:h-8 sm:w-auto"
            onClick={() => setFormOpen(true)}
          >
            <FileText size={12} className="mr-1.5" />
            Fill out form
          </Button>
          <FormSubmissionDialog
            open={formOpen}
            onOpenChange={setFormOpen}
            formId={form.formId}
            anchor={{
              kind: "hiring",
              jobPostId,
              hiringProcessItemId: form.hiringProcessItemId,
            }}
            suggestionsEnabled
            showSuccessToast={false}
            onCompleted={onCompleted}
          />
        </>
      )}
    </li>
  );
}

function ApplicationFormsSkeleton() {
  return (
    <div role="status" aria-busy="true" className="my-4">
      <span className="sr-only">Loading screening forms</span>
      <div
        aria-hidden
        className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center"
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Skeleton aria-hidden className="size-8 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton aria-hidden className="h-4 w-2/3" />
            <Skeleton aria-hidden className="h-3 w-full" />
          </div>
        </div>
        <Skeleton
          aria-hidden
          className="h-10 w-full sm:h-8 sm:w-28 sm:shrink-0"
        />
      </div>
    </div>
  );
}

/**
 * The screening forms submitted together with an application: a skeleton
 * while they load, a retry on failure, then one row per form. Renders
 * nothing for a job post without screening forms.
 */
export function ApplicationFormsSection({
  status,
  forms,
  jobPostId,
  onRefetch,
}: {
  status: "pending" | "error" | "success";
  forms: ApplicationForm[];
  jobPostId: string;
  onRefetch: () => void;
}) {
  if (status === "pending") {
    return <ApplicationFormsSkeleton />;
  }
  if (status === "error") {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertTitle>Couldn&apos;t load the screening form</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>Check your connection and try again.</p>
          <Button type="button" variant="outline" size="sm" onClick={onRefetch}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  if (forms.length === 0) {
    return null;
  }
  return (
    <section className="my-4" aria-label="Screening forms">
      <ul className="flex flex-col gap-2">
        {forms.map((form) => (
          <ApplicationFormRow
            key={form.hiringProcessItemId}
            form={form}
            jobPostId={jobPostId}
            onCompleted={onRefetch}
          />
        ))}
      </ul>
    </section>
  );
}
